import React, { ReactNode } from 'react';

import { random, randomArrayElement } from 'phoenix2/utils/random_ts';

/*
    Functions to generate comments to appear on the result screen.
    These comments are randomly selected from a set, and may or may not include
    the student's name (whether to do so is chosen in advance or at random).

    These comments should respond to the student's state when answering this
    question. For example, the comment when answering correctly first time will
    be different to the comment you get if you've previously failed this
    question.
 */

// The situations we can generate remarks for.
enum Situation {
    None,
    CorrectAnswer,
    WrongAnswer,
    TaskComplete,
}

// Different conditions change the remark that is generated.
enum Condition {
    None,
    PreviouslyAnsweredWrong,
    ShouldWatchVideo,
}

// This text string will only be used as a marker to replace with the student's
// name before a remark is output
const STUDENT_NAME_PLACEHOLDER = '<name>';
const DEFAULT_USE_NAME_PROBABILITY = 0.5;

const getRemark = (
    remarksWithNames: string[],
    remarksWithoutNames: string[],
    studentName?: string,
): string => {
    const remarksToUse = studentName ? remarksWithNames : remarksWithoutNames;
    const remark = randomArrayElement(remarksToUse);
    return studentName ? remark.replace(STUDENT_NAME_PLACEHOLDER, studentName) : remark;
};

// The set of remarks that can be shown to a user on the "Correct!" view
function getCorrectRemark(condition: Condition, studentName?: string): ReactNode {
    switch (condition) {
        case Condition.PreviouslyAnsweredWrong:
            return getRemark(
                [
                    `You’re doing really well, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Your hard work has paid off, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `You stuck with it, ${STUDENT_NAME_PLACEHOLDER}. Well done!`,
                ],
                [
                    'You’re doing really well!',
                    'Your hard work has paid off!',
                    'You stuck with it. Well done!',
                ],
                studentName,
            );
        case Condition.None:
        default:
            return getRemark(
                [
                    `Good work, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Great work, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Fantastic, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Excellent, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Great effort, ${STUDENT_NAME_PLACEHOLDER}!`,
                ],
                ['Good work!', 'Great work!', 'Fantastic!', 'Excellent!', 'Great effort!'],
                studentName,
            );
    }
}

// The set of remarks that can be shown to a user on the "Incorrect" view
function getWrongRemark(condition: Condition, studentName?: string): ReactNode {
    switch (condition) {
        case Condition.ShouldWatchVideo:
            return (
                <span className="result-subtitle-prominent">
                    <div className="result-subtitle-prominent-bold">
                        {getRemark(
                            [`Why not watch the video, ${STUDENT_NAME_PLACEHOLDER}?`],
                            ['Why not watch the video?'],
                            studentName,
                        )}
                    </div>
                    You seem to be having some trouble
                </span>
            );
        case Condition.None:
        default:
            return getRemark(
                [
                    `Try again, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Have another go, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Keep on trying, ${STUDENT_NAME_PLACEHOLDER}!`,
                    `Good effort, ${STUDENT_NAME_PLACEHOLDER}. Next time!`,
                    `Stay confident, ${STUDENT_NAME_PLACEHOLDER}. Keep going!`,
                ],
                [
                    'Try again!',
                    'Have another go!',
                    'Keep on trying!',
                    'Good effort. Next time!',
                    'Stay confident. Keep going!',
                ],
                studentName,
            );
    }
}

// The set of remarks that can be shown to a user on the task complete alert
function getTaskCompleteRemark(studentName?: string): ReactNode {
    return getRemark(
        [
            `Great effort, ${STUDENT_NAME_PLACEHOLDER}!`,
            `Congratulations, ${STUDENT_NAME_PLACEHOLDER}!`,
            `Nice work, ${STUDENT_NAME_PLACEHOLDER}!`,
        ],
        ['Great effort!', 'Congratulations!', 'Nice work!'],
        studentName,
    );
}

function getSituation(situationName: string): Situation {
    switch (situationName) {
        case 'correct':
            return Situation.CorrectAnswer;
        case 'wrong':
            return Situation.WrongAnswer;
        case 'task-complete':
            return Situation.TaskComplete;
        default:
            return Situation.None;
    }
}

function getCondition(situation: Situation, taskItemState?: any): Condition {
    const wrongAnswerCount: number = taskItemState?.get('wrongAnswers', 0) || 0;
    if (situation === Situation.CorrectAnswer && wrongAnswerCount > 0) {
        // If the question had been answered wrong before, show a message
        // acknowledging the student has put in extra effort
        return Condition.PreviouslyAnsweredWrong;
    } else if (situation === Situation.WrongAnswer && wrongAnswerCount === 3) {
        // If the question has been answered wrong 3 times show a nudge to watch the video
        return Condition.ShouldWatchVideo;
    }
    return Condition.None;
}

function getUseNameProbability(useNameProbability?: number): number {
    return useNameProbability === undefined ? DEFAULT_USE_NAME_PROBABILITY : useNameProbability;
}

// calculateRemark calculates a personalised remark based on the specified situation
export function calculateRemark(
    situationName: string,
    studentName?: string,
    taskItemState?: any,
    useNameProbability?: number,
): ReactNode {
    const situation = getSituation(situationName);
    const condition = getCondition(situation, taskItemState);

    if (random(0, 1) > getUseNameProbability(useNameProbability)) {
        studentName = undefined;
    }

    switch (situation) {
        case Situation.CorrectAnswer:
            return getCorrectRemark(condition, studentName);
        case Situation.WrongAnswer:
            return getWrongRemark(condition, studentName);
        case Situation.TaskComplete:
            return getTaskCompleteRemark(studentName);
        case Situation.None:
        default:
            return undefined;
    }
}

// export some code just for testing
export const _testExports = {
    Situation,
    Condition,
    STUDENT_NAME_PLACEHOLDER,
    DEFAULT_USE_NAME_PROBABILITY,
    getRemark,
    getCondition,
    getUseNameProbability,
};
